import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { chatApi } from "../../api/chat";
import type { ChatChannel, ChatPerson } from "../../api/chat";
import type { ApiError } from "../../api/client";
import { useConfirm } from "../../components/ConfirmDialog";
import Ic from "../../components/icons.jsx";
import { ChatAvatar } from "./ChatAvatar";
import { channelSlug, fold, matchPeople } from "./chatFormat";
import { roleLabel } from "./chatPeople";

/** The chat's dialog frame: Escape and a click outside close it, focus starts on its first field. */
function Modal({ title, onClose, children, footer, wide }: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  const panel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    panel.current?.querySelector<HTMLElement>("input, textarea, button:not(.chat-modal-x)")?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      previous?.focus?.();
    };
  }, [onClose]);
  // A click outside closes the dialog, as Escape does from the keyboard.
  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div className="chat-modal-back" onMouseDown={onClose}>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <div
        ref={panel}
        className={`chat-modal${wide ? " wide" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="chat-modal-head">
          <h3>{title}</h3>
          <button type="button" className="chat-tool chat-modal-x" onClick={onClose} aria-label="×">
            <Ic.x />
          </button>
        </div>
        <div className="chat-modal-body">{children}</div>
        {footer && <div className="chat-modal-foot">{footer}</div>}
      </div>
    </div>
  );
}

/** Colleagues picked for a channel: chips above a search field that offers the rest. */
function PeoplePicker({ people, chosen, onChange, exclude = [] }: {
  people: ChatPerson[];
  chosen: string[];
  onChange: (ids: string[]) => void;
  exclude?: string[];
}) {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const byId = useMemo(() => new Map(people.map((p) => [p.id, p])), [people]);
  const offered = matchPeople(people.filter((p) => !chosen.includes(p.id) && !exclude.includes(p.id)), q, 6);
  return (
    <div className="chat-people-picker">
      {chosen.length > 0 && (
        <div className="chat-chips">
          {chosen.map((id) => (
            <span key={id} className="chat-chip">
              <ChatAvatar person={byId.get(id)} id={id} size="xs" />
              {byId.get(id)?.name ?? id}
              <button
                type="button"
                aria-label={t("chat.removePerson", "Remove")}
                onClick={() => onChange(chosen.filter((x) => x !== id))}
              >
                <Ic.x />
              </button>
            </span>
          ))}
        </div>
      )}
      <input
        className="input"
        value={q}
        placeholder={t("chat.findPeople", "Find a colleague by name")}
        aria-label={t("chat.findPeople", "Find a colleague by name")}
        onChange={(e) => setQ(e.target.value)}
      />
      <div className="chat-people-offer">
        {offered.map((p) => (
          <button key={p.id} type="button" className="chat-person-row" onClick={() => { onChange([...chosen, p.id]); setQ(""); }}>
            <ChatAvatar person={p} id={p.id} size="sm" online={p.online} />
            <span className="chat-person-name">{p.name}</span>
            <small>{roleLabel(p, t)}</small>
            <Ic.plus className="chat-person-add" />
          </button>
        ))}
      </div>
    </div>
  );
}

export function NewChannelDialog({ people, me, onClose, onCreated }: {
  people: ChatPerson[];
  me: string;
  onClose: () => void;
  onCreated: (channel: ChatChannel) => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [topic, setTopic] = useState("");
  const [privateChannel, setPrivate] = useState(false);
  const [members, setMembers] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const slug = channelSlug(name);

  async function create() {
    if (!slug || busy) return;
    setBusy(true);
    setError(null);
    try {
      const channel = await chatApi.createChannel({
        name, topic: topic.trim() || undefined, privateChannel, memberIds: privateChannel ? members : undefined,
      });
      onCreated(channel);
    } catch (e) {
      const err = e as ApiError;
      setError(err.code === "CHANNEL_NAME_TAKEN"
        ? t("chat.nameTaken", "A channel with that name is already open.")
        : err.message);
      setBusy(false);
    }
  }

  return (
    <Modal
      title={t("chat.newChannel", "New channel")}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>{t("chat.cancel", "Cancel")}</button>
          <button type="button" className="btn primary" onClick={create} disabled={!slug || busy}>
            {t("chat.create", "Create channel")}
          </button>
        </>
      }
    >
      <label className="chat-field">
        <span>{t("chat.channelName", "Name")}</span>
        <div className="chat-name-field">
          <Ic.hash />
          <input
            className="input"
            value={name}
            maxLength={80}
            placeholder={t("chat.channelNamePlaceholder", "e.g. sales-serbia")}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") void create(); }}
          />
        </div>
        <small>
          {slug && slug !== name.trim()
            ? t("chat.nameWillBe", "It will be called #{{name}}.", { name: slug })
            : t("chat.nameRules", "Lower case, no spaces. Name it after what it is for: a team, a customer, a job.")}
        </small>
      </label>
      <label className="chat-field">
        <span>{t("chat.topic", "Topic")} <em>{t("chat.optional", "(optional)")}</em></span>
        <input
          className="input"
          value={topic}
          maxLength={250}
          placeholder={t("chat.topicPlaceholder", "What the channel is for")}
          onChange={(e) => setTopic(e.target.value)}
        />
      </label>
      <div className="chat-visibility" role="radiogroup" aria-label={t("chat.visibility", "Who sees it")}>
        <button type="button" role="radio" aria-checked={!privateChannel}
          className={`chat-option${!privateChannel ? " on" : ""}`} onClick={() => setPrivate(false)}>
          <Ic.hash />
          <span>
            <b>{t("chat.public", "Public")}</b>
            <small>{t("chat.publicHint", "Anyone at Bankom can find it and join.")}</small>
          </span>
        </button>
        <button type="button" role="radio" aria-checked={privateChannel}
          className={`chat-option${privateChannel ? " on" : ""}`} onClick={() => setPrivate(true)}>
          <Ic.lock />
          <span>
            <b>{t("chat.private", "Private")}</b>
            <small>{t("chat.privateHint", "Only the people you add see it.")}</small>
          </span>
        </button>
      </div>
      {privateChannel && (
        <div className="chat-field">
          <span>{t("chat.addPeople", "Add people")}</span>
          <PeoplePicker people={people} chosen={members} onChange={setMembers} exclude={[me]} />
        </div>
      )}
      {error && <p className="chat-error" role="alert">{error}</p>}
    </Modal>
  );
}

export function BrowseChannelsDialog({ channels, onClose, onOpen, onJoin, onNew }: {
  channels: ChatChannel[];
  onClose: () => void;
  onOpen: (channel: ChatChannel) => void;
  onJoin: (channel: ChatChannel) => Promise<void>;
  onNew: () => void;
}) {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const [joining, setJoining] = useState<string | null>(null);
  const list = channels
    .filter((c) => !c.privateChannel)
    .filter((c) => !q || fold(c.name).includes(fold(q)) || fold(c.topic ?? "").includes(fold(q)));
  return (
    <Modal
      title={t("chat.browse", "Browse channels")}
      onClose={onClose}
      wide
      footer={<button type="button" className="btn" onClick={onNew}><Ic.plus /> {t("chat.newChannel", "New channel")}</button>}
    >
      <input
        className="input"
        value={q}
        placeholder={t("chat.findChannel", "Find a channel")}
        aria-label={t("chat.findChannel", "Find a channel")}
        onChange={(e) => setQ(e.target.value)}
      />
      <div className="chat-browse">
        {list.length === 0 && <p className="chat-muted">{t("chat.noChannelsFound", "No channel by that name.")}</p>}
        {list.map((c) => (
          <div key={c.id} className="chat-browse-row">
            <button type="button" className="chat-browse-main" onClick={() => onOpen(c)}>
              <b><Ic.hash /> {c.name}</b>
              <small>
                {t("chat.memberCount", "{{count}} members", { count: c.memberCount })}
                {c.topic ? ` · ${c.topic}` : ""}
              </small>
            </button>
            {c.member ? (
              <span className="chat-joined"><Ic.check /> {t("chat.joined", "Joined")}</span>
            ) : (
              <button
                type="button"
                className="btn sm primary"
                disabled={joining === c.id}
                onClick={async () => { setJoining(c.id); try { await onJoin(c); } finally { setJoining(null); } }}
              >
                {t("chat.join", "Join")}
              </button>
            )}
          </div>
        ))}
      </div>
    </Modal>
  );
}

export function MembersDialog({ channel, people, online, me, onClose, onMessage }: {
  channel: ChatChannel;
  people: ChatPerson[];
  online: Set<string>;
  me: string;
  onClose: () => void;
  /** Opens the conversation with one of the members. */
  onMessage: (personId: string) => void;
}) {
  const { t } = useTranslation();
  const [members, setMembers] = useState<string[] | null>(null);
  const [adding, setAdding] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const byId = useMemo(() => new Map(people.map((p) => [p.id, p])), [people]);

  useEffect(() => {
    let live = true;
    chatApi.members(channel.id).then((ids) => { if (live) setMembers(ids); }).catch((e: ApiError) => setError(e.message));
    return () => { live = false; };
  }, [channel.id]);

  async function add() {
    if (adding.length === 0) return;
    setBusy(true);
    try {
      setMembers(await chatApi.addMembers(channel.id, adding));
      setAdding([]);
    } catch (e) {
      setError((e as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  const sorted = (members ?? [])
    .map((id) => ({ id, person: byId.get(id) }))
    .sort((a, b) => Number(online.has(b.id)) - Number(online.has(a.id))
      || (a.person?.name ?? "").localeCompare(b.person?.name ?? ""));

  return (
    <Modal title={t("chat.membersOf", "Members of #{{name}}", { name: channel.name })} onClose={onClose} wide>
      {channel.member && !channel.defaultChannel && (
        <div className="chat-field">
          <span>{t("chat.addPeople", "Add people")}</span>
          <PeoplePicker people={people} chosen={adding} onChange={setAdding} exclude={members ?? []} />
          {adding.length > 0 && (
            <button type="button" className="btn primary sm chat-add-btn" onClick={add} disabled={busy}>
              {t("chat.addToChannel", "Add to channel")}
            </button>
          )}
        </div>
      )}
      {error && <p className="chat-error" role="alert">{error}</p>}
      <div className="chat-members">
        {members === null && <p className="chat-muted">{t("chat.loading", "Loading…")}</p>}
        {sorted.map(({ id, person }) => (
          <div key={id} className="chat-person-row static">
            <ChatAvatar person={person} id={id} size="sm" online={online.has(id)} />
            <span className="chat-person-name">{person?.name ?? t("chat.unknownPerson", "former colleague")}</span>
            <small>{person ? roleLabel(person, t) : ""}</small>
            {person && id !== me && (
              <button type="button" className="btn sm chat-person-action" onClick={() => onMessage(id)}>
                <Ic.chat /> {t("chat.sendDirect", "Message")}
              </button>
            )}
          </div>
        ))}
      </div>
    </Modal>
  );
}

export function EditChannelDialog({ channel, onClose, onSaved, onArchived }: {
  channel: ChatChannel;
  onClose: () => void;
  onSaved: (channel: ChatChannel) => void;
  onArchived: () => void;
}) {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [name, setName] = useState(channel.name);
  const [topic, setTopic] = useState(channel.topic ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const slug = channelSlug(name);

  async function save() {
    if (!slug || busy) return;
    setBusy(true);
    setError(null);
    try {
      onSaved(await chatApi.updateChannel(channel.id, { name, topic: topic.trim() }));
    } catch (e) {
      const err = e as ApiError;
      setError(err.code === "CHANNEL_NAME_TAKEN" ? t("chat.nameTaken", "A channel with that name is already open.") : err.message);
      setBusy(false);
    }
  }

  async function archive() {
    const ok = await confirm({
      title: t("chat.archiveTitle", "Close #{{name}}?", { name: channel.name }),
      message: t("chat.archiveMessage", "The channel leaves everyone's list and nobody can write in it any more."),
      confirmLabel: t("chat.archive", "Close channel"),
      danger: true,
    });
    if (!ok) return;
    try {
      await chatApi.archiveChannel(channel.id);
      onArchived();
    } catch (e) {
      setError((e as ApiError).message);
    }
  }

  return (
    <Modal
      title={t("chat.editChannel", "Channel settings")}
      onClose={onClose}
      footer={
        <>
          {!channel.defaultChannel && (
            <button type="button" className="btn chat-danger" onClick={archive}>
              <Ic.trash /> {t("chat.archive", "Close channel")}
            </button>
          )}
          <span style={{ flex: 1 }} />
          <button type="button" className="btn" onClick={onClose}>{t("chat.cancel", "Cancel")}</button>
          <button type="button" className="btn primary" onClick={save} disabled={!slug || busy}>{t("chat.save", "Save")}</button>
        </>
      }
    >
      <label className="chat-field">
        <span>{t("chat.channelName", "Name")}</span>
        <div className="chat-name-field">
          {channel.privateChannel ? <Ic.lock /> : <Ic.hash />}
          <input className="input" value={name} maxLength={80} onChange={(e) => setName(e.target.value)} />
        </div>
        {slug && slug !== name.trim() && <small>{t("chat.nameWillBe", "It will be called #{{name}}.", { name: slug })}</small>}
      </label>
      <label className="chat-field">
        <span>{t("chat.topic", "Topic")} <em>{t("chat.optional", "(optional)")}</em></span>
        <textarea className="input chat-textarea" rows={3} value={topic} maxLength={250} onChange={(e) => setTopic(e.target.value)} />
      </label>
      {error && <p className="chat-error" role="alert">{error}</p>}
    </Modal>
  );
}

/** Picks the colleague to write to: those on now first, then by name; a name typed narrows the list. */
export function NewDirectDialog({ people, me, online, onClose, onPick }: {
  people: ChatPerson[];
  me: string;
  online: Set<string>;
  onClose: () => void;
  onPick: (personId: string) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const others = people.filter((p) => p.id !== me);
  const list = q
    ? matchPeople(others, q, 50)
    : [...others].sort((a, b) => Number(online.has(b.id)) - Number(online.has(a.id)) || a.name.localeCompare(b.name));

  async function pick(id: string) {
    setBusy(true);
    setError(null);
    try {
      await onPick(id);
    } catch (e) {
      setError((e as ApiError).message);
      setBusy(false);
    }
  }

  return (
    <Modal title={t("chat.newDirect", "New direct message")} onClose={onClose} wide>
      <input
        className="input"
        value={q}
        placeholder={t("chat.findPeople", "Find a colleague by name")}
        aria-label={t("chat.findPeople", "Find a colleague by name")}
        onChange={(e) => setQ(e.target.value)}
      />
      <div className="chat-members">
        {list.length === 0 && <p className="chat-muted">{t("chat.noPeopleFound", "Nobody by that name.")}</p>}
        {list.map((p) => (
          <button key={p.id} type="button" className="chat-person-row" disabled={busy} onClick={() => pick(p.id)}>
            <ChatAvatar person={p} id={p.id} size="sm" online={online.has(p.id)} />
            <span className="chat-person-name">{p.name}</span>
            <small>{roleLabel(p, t)}</small>
          </button>
        ))}
      </div>
      {error && <p className="chat-error" role="alert">{error}</p>}
    </Modal>
  );
}
